import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Card, Grid, Button, Chip, IconButton,
  ToggleButton, ToggleButtonGroup, TextField, CircularProgress,
  Alert, Divider, Tooltip
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, AutoAwesome, Summarize, Block,
  PieChart, Warning, Group, HelpOutline, ContentCopy, Refresh
} from '@mui/icons-material';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/es';
import { aiAPI } from '../../services/api';
import DevBadge from '../../components/common/DevBadge';

dayjs.extend(isoWeek);
dayjs.locale('es');

const ANALYSIS_TYPES = [
  {
    id: 'summary',
    label: 'Resumen General',
    icon: <Summarize />,
    description: 'Visión global del período: horas, proyectos activos y rendimiento del equipo',
  },
  {
    id: 'blockages',
    label: 'Bloqueos',
    icon: <Block />,
    description: 'Análisis de impedimentos registrados y su impacto en el avance',
  },
  {
    id: 'distribution',
    label: 'Distribución de Tiempo',
    icon: <PieChart />,
    description: 'Cómo se distribuyen las horas entre categorías, proyectos y miembros',
  },
  {
    id: 'risk',
    label: 'Riesgo de Proyecto',
    icon: <Warning />,
    description: 'Identifica proyectos con signos de retraso, sobrecarga o desequilibrio',
  },
  {
    id: 'members',
    label: 'Análisis por Miembro',
    icon: <Group />,
    description: 'Rendimiento individual, carga de trabajo y alertas por persona',
  },
  {
    id: 'custom',
    label: 'Pregunta Libre',
    icon: <HelpOutline />,
    description: 'Escribe tu propia pregunta sobre los datos del período',
  },
];

function PeriodNavigator({ periodMode, setPeriodMode, weekStart, setWeekStart, monthDate, setMonthDate, periodLabel }) {
  const handlePrev = () => {
    if (periodMode === 'week') setWeekStart(w => w.subtract(1, 'week'));
    else setMonthDate(m => m.subtract(1, 'month'));
  };
  const handleNext = () => {
    if (periodMode === 'week') setWeekStart(w => w.add(1, 'week'));
    else setMonthDate(m => m.add(1, 'month'));
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <ToggleButtonGroup
        value={periodMode}
        exclusive
        onChange={(_, v) => v && setPeriodMode(v)}
        size="small"
        sx={{ '& .MuiToggleButton-root': { height: 32, px: 2, fontSize: '0.8rem' } }}
      >
        <ToggleButton value="week">Semana</ToggleButton>
        <ToggleButton value="month">Mes</ToggleButton>
      </ToggleButtonGroup>

      <IconButton size="small" onClick={handlePrev} sx={{ height: 32, width: 32 }}>
        <ChevronLeft fontSize="small" />
      </IconButton>

      <Chip
        label={periodLabel}
        size="small"
        sx={{ height: 32, borderRadius: 1, fontWeight: 600, fontSize: '0.8rem', px: 0.5 }}
      />

      <IconButton size="small" onClick={handleNext} sx={{ height: 32, width: 32 }}>
        <ChevronRight fontSize="small" />
      </IconButton>
    </Box>
  );
}

function ContextSummary({ ctx }) {
  if (!ctx) return null;
  const periodLabel = ctx.period
    ? typeof ctx.period === 'string'
      ? ctx.period
      : `${ctx.period.startDate ?? ''} – ${ctx.period.endDate ?? ''}`
    : '';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
      <Chip label={`Período: ${periodLabel}`} size="small" variant="outlined" />
      {ctx.summary?.totalHours != null && (
        <Chip label={`${ctx.summary.totalHours} h registradas`} size="small" variant="outlined" />
      )}
      {ctx.summary?.memberCount != null && (
        <Chip label={`${ctx.summary.memberCount} miembro${ctx.summary.memberCount !== 1 ? 's' : ''}`} size="small" variant="outlined" />
      )}
      {ctx.summary?.projectCount != null && (
        <Chip label={`${ctx.summary.projectCount} proyecto${ctx.summary.projectCount !== 1 ? 's' : ''}`} size="small" variant="outlined" />
      )}
      {ctx.summary?.blockageCount != null && ctx.summary.blockageCount > 0 && (
        <Chip label={`${ctx.summary.blockageCount} bloqueo${ctx.summary.blockageCount !== 1 ? 's' : ''}`} size="small" variant="outlined" color="warning" />
      )}
    </Box>
  );
}

function AnswerBlock({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Render simple markdown-like formatting: **bold**, headings via ##, bullet lines
  const lines = text.split('\n');

  const renderLine = (line, idx) => {
    // Heading
    if (line.startsWith('## ')) {
      return (
        <Typography key={idx} variant="subtitle1" fontWeight={700} mt={2} mb={0.5}>
          {line.slice(3)}
        </Typography>
      );
    }
    if (line.startsWith('# ')) {
      return (
        <Typography key={idx} variant="h6" fontWeight={700} mt={2} mb={0.5}>
          {line.slice(2)}
        </Typography>
      );
    }
    // Bullet
    if (line.startsWith('- ') || line.startsWith('• ')) {
      const content = line.slice(2);
      return (
        <Typography key={idx} variant="body2" sx={{ pl: 2, lineHeight: 1.9, display: 'flex', gap: 1 }}>
          <span style={{ color: 'rgb(132,0,255)', fontWeight: 700, flexShrink: 0 }}>•</span>
          <span dangerouslySetInnerHTML={{ __html: boldify(content) }} />
        </Typography>
      );
    }
    // Numbered
    if (/^\d+\.\s/.test(line)) {
      return (
        <Typography key={idx} variant="body2" sx={{ pl: 2, lineHeight: 1.9 }}
          dangerouslySetInnerHTML={{ __html: boldify(line) }} />
      );
    }
    // Empty line
    if (line.trim() === '') return <Box key={idx} sx={{ height: 8 }} />;
    // Normal paragraph
    return (
      <Typography key={idx} variant="body2" sx={{ lineHeight: 1.9 }}
        dangerouslySetInnerHTML={{ __html: boldify(line) }} />
    );
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title={copied ? '¡Copiado!' : 'Copiar respuesta'} placement="top">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ position: 'absolute', top: 0, right: 0 }}
        >
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
      <Box sx={{ pr: 4 }}>
        {lines.map(renderLine)}
      </Box>
    </Box>
  );
}

// Simple **bold** converter (XSS-safe: only replaces ** markers, no raw HTML accepted)
function boldify(text) {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

export default function AIAnalysis() {
  // ── Period state ──────────────────────────────────────────────────────────
  const [periodMode, setPeriodMode] = useState('week');
  const [weekStart, setWeekStart] = useState(dayjs().startOf('isoWeek'));
  const [monthDate, setMonthDate] = useState(dayjs().startOf('month'));

  // ── Analysis state ────────────────────────────────────────────────────────
  const [selectedType, setSelectedType] = useState('summary');
  const [customQuestion, setCustomQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);   // { answer, context, usage }
  const [error, setError] = useState(null);

  // ── Derived period ────────────────────────────────────────────────────────
  const { startDate, endDate, periodLabel } = useMemo(() => {
    if (periodMode === 'week') {
      const s = weekStart;
      const e = weekStart.endOf('isoWeek');
      const weekNum = s.isoWeek();
      return {
        startDate: s.format('YYYY-MM-DD'),
        endDate: e.format('YYYY-MM-DD'),
        periodLabel: `Sem ${weekNum} · ${s.format('D MMM')} – ${e.format('D MMM YYYY')}`,
      };
    } else {
      const s = monthDate;
      const e = monthDate.endOf('month');
      return {
        startDate: s.format('YYYY-MM-DD'),
        endDate: e.format('YYYY-MM-DD'),
        periodLabel: s.format('MMMM YYYY').replace(/^\w/, c => c.toUpperCase()),
      };
    }
  }, [periodMode, weekStart, monthDate]);

  // ── Analyze ───────────────────────────────────────────────────────────────
  const handleAnalyze = async () => {
    if (selectedType === 'custom' && !customQuestion.trim()) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const payload = { startDate, endDate, analysisType: selectedType };
      if (selectedType === 'custom') payload.customQuestion = customQuestion.trim();
      const res = await aiAPI.analyze(payload);
      setResult(res.data);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error || err.message;
      if (status === 503) {
        setError('La clave de OpenAI no está configurada. Configura OPENAI_API_KEY en el servidor.');
      } else if (status === 401) {
        setError('La clave de OpenAI es inválida. Verifica OPENAI_API_KEY en el servidor.');
      } else if (status === 429) {
        setError('Límite de uso de OpenAI alcanzado. Intenta de nuevo en unos momentos.');
      } else {
        setError(msg || 'Error al consultar la IA. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
  };

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <DevBadge />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <AutoAwesome sx={{ color: 'rgb(132,0,255)', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700}>
          Análisis con IA
        </Typography>
        <Chip
          label="beta"
          size="small"
          sx={{
            bgcolor: 'rgb(132,0,255)', color: '#fff',
            fontWeight: 700, fontSize: '0.65rem', height: 20, borderRadius: 1
          }}
        />
      </Box>

      {/* ── Period Selector ─────────────────────────────────────────────── */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          PERÍODO DE ANÁLISIS
        </Typography>
        <PeriodNavigator
          periodMode={periodMode} setPeriodMode={setPeriodMode}
          weekStart={weekStart} setWeekStart={setWeekStart}
          monthDate={monthDate} setMonthDate={setMonthDate}
          periodLabel={periodLabel}
        />
      </Card>

      {/* ── Analysis Type Cards ─────────────────────────────────────────── */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          TIPO DE ANÁLISIS
        </Typography>
        <Grid container spacing={1.5}>
          {ANALYSIS_TYPES.map(type => {
            const active = selectedType === type.id;
            return (
              <Grid item xs={12} sm={6} md={4} key={type.id}>
                <Card
                  variant="outlined"
                  onClick={() => { setSelectedType(type.id); handleReset(); }}
                  sx={{
                    p: 1.5, cursor: 'pointer', transition: 'all 0.2s',
                    borderColor: active ? 'rgb(132,0,255)' : 'divider',
                    bgcolor: active ? 'rgba(132,0,255,0.06)' : 'background.paper',
                    '&:hover': {
                      borderColor: 'rgb(132,0,255)',
                      bgcolor: 'rgba(132,0,255,0.04)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ color: active ? 'rgb(132,0,255)' : 'text.secondary', display: 'flex' }}>
                      {type.icon}
                    </Box>
                    <Typography variant="body2" fontWeight={active ? 700 : 500}>
                      {type.label}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {type.description}
                  </Typography>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Custom question field */}
        {selectedType === 'custom' && (
          <Box mt={2}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              size="small"
              label="Tu pregunta"
              placeholder="Ej: ¿Qué miembro tiene mayor riesgo de burnout este mes?"
              value={customQuestion}
              onChange={e => setCustomQuestion(e.target.value)}
              inputProps={{ maxLength: 500 }}
              helperText={`${customQuestion.length}/500`}
            />
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleAnalyze}
            disabled={loading || (selectedType === 'custom' && !customQuestion.trim())}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
            sx={{
              bgcolor: 'rgb(132,0,255)',
              '&:hover': { bgcolor: 'rgba(132,0,255,0.85)' },
              '&:disabled': { opacity: 0.6 },
              height: 40, px: 3
            }}
          >
            {loading ? 'Consultando con IA…' : 'Analizar'}
          </Button>
        </Box>
      </Card>

      {/* ── Loading ─────────────────────────────────────────────────────── */}
      {loading && (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ color: 'rgb(132,0,255)', mb: 2 }} />
          <Typography color="text.secondary">
            Analizando datos del período con IA…
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Esto puede tardar unos segundos
          </Typography>
        </Card>
      )}

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {error && !loading && (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={handleReset} startIcon={<Refresh />}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* ── Result ──────────────────────────────────────────────────────── */}
      {result && !loading && (
        <Card sx={{ p: 3 }}>
          {/* Result header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AutoAwesome sx={{ color: 'rgb(132,0,255)', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {ANALYSIS_TYPES.find(t => t.id === selectedType)?.label}
            </Typography>
          </Box>

          {/* Context summary chips */}
          <ContextSummary ctx={result.context} />

          <Divider sx={{ my: 2 }} />

          {/* Answer */}
          <AnswerBlock text={result.answer} />

          {/* Usage info */}
          {result.usage && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="caption" color="text.disabled">
                Tokens usados: {result.usage.total_tokens ?? '—'}
              </Typography>
            </Box>
          )}
        </Card>
      )}
    </Box>
  );
}
